import { format, parseISO } from 'date-fns'
import { enGB } from 'date-fns/locale'

import { DateTime } from 'luxon'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useAppSelector } from '../../../app/hooks'
import { BlastContext } from '../../../providers/blast/BlastContext'
import { SessionContext } from '../../../providers/session/SessionContext'
import { log } from '../../../services/LogService'
import { getSettings, shouldHide, validate } from '../../../services/ValidationService'
import { isDate, notDefined } from '../../../utils/TypeCheckers'
import { extractExtraFieldProperties, formatDate, getDateFormat, RunOnceEffect, unSubscribe } from '../../../utils/Utils'
import { FieldProperties, FormMode } from '../../types'
import { watchBlastDeltas } from '../../ui/FieldUtils'
import FieldLabel from '../field-label/FieldLabel'
import { GuslFormState } from '../maintain-form/guslFormSlice'
import { ExtraFieldProperties } from '../money/types'
import { FieldContentStyled, FieldContentWrapperStyled, FloatingFormStyled, HintStyled, TextFieldStyled } from '../text/styled'
import { BROWSER_TIMEZONE } from '../timezone/TimezoneField'
import { DateFloatingFormStyled, DatePickerStyled, DateStyled } from './styled'

export const DateField = (properties: FieldProperties): React.ReactElement<FieldProperties> => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [className] = React.useState<string>(() => 'DateField-' + new Date().getTime())

    const sessionContext = useContext(SessionContext)
    const blastContext = useContext(BlastContext)

    const _guslFormState: GuslFormState = useAppSelector((state) => state.guslFormSlice[properties.code])

    const [formMode, setFormMode] = useState(properties.formMode)
    const valueRef = useRef(properties?.data)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [submitted, setSubmitted] = useState(false)
    const [refreshCounter, setRefreshCounter] = useState<number>(0)
    const [extraFieldProperties] = useState<ExtraFieldProperties>(() => extractExtraFieldProperties(properties))
    const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone)
    const [showTime] = useState<boolean>((properties.fieldConfig.dateFormat || '').indexOf(':mm') > 0)
    const [dateFormat, setDateFormat] = useState<string>(() =>
        getDateFormat(properties?.fieldConfig?.dateFormat, extraFieldProperties?.timeZone || timezone, false)
    )

    const [localeLoaded, setLocaleLoaded] = useState<boolean>(false)

    const convertToUTC = (_timezone?: string) => {
        if (_guslFormState?.getFieldValue(properties) || properties?.data) {
            try {
                if (_guslFormState?.getFieldValue(properties) || properties?.data) {
                    try {
                        const dateStr = _guslFormState?.getFieldValue(properties) || properties?.data || ''
                        const utcDateTime = new Date(_guslFormState?.getFieldValue(properties) || properties?.data)
                        if (typeof dateStr === 'string') {
                            return new Date(
                                showTime ? utcDateTime.getFullYear() : utcDateTime.getUTCFullYear(),
                                showTime ? utcDateTime.getMonth() : utcDateTime.getUTCMonth(),
                                showTime ? utcDateTime.getDate() : utcDateTime.getUTCDate(),
                                showTime ? utcDateTime.getHours() : 0,
                                showTime ? utcDateTime.getMinutes() : 0,
                                showTime ? utcDateTime.getSeconds() : 0
                            )
                        } else {
                            return new Date(utcDateTime.getFullYear(), utcDateTime.getMonth(), utcDateTime.getDate(), 0, 0, 0)
                        }
                    } catch (err) {
                        console.error('-- error --', err)
                        log.error(className, 'ERR001', 'Failed to parse date', err)
                        return null
                    }
                }
            } catch (err) {
                console.error('-- error --', err)
                log.error(className, 'ERR001', 'Failed to parse date', err)
                return null
            }
        }
        return null
    }

    const [formValue, setFormValue] = useState<Date | null>(() => convertToUTC())

    const extractTableValue = (_timezone?: string) => {
        if (_guslFormState?.getFieldValue(properties) || properties?.data) {
            try {
                const useTimezone: string = extraFieldProperties?.timeZone || _timezone || timezone
                return formatDate(
                    parseISO(_guslFormState?.getFieldValue(properties) || properties?.data || ''),
                    properties.fieldConfig.dateFormat || 'DD-MM-YYYY',
                    useTimezone
                )
            } catch (err) {
                console.error('-- error --', err)
                log.error(className, 'ERR001', 'Failed to parse date', err)
                return format(parseISO(_guslFormState?.getFieldValue(properties) || properties?.data || ''), 'dd-MM-yyyy HH:mm')
            }
        }
        return ''
    }
    const [tableValue, setTableValue] = useState<string>(() => extractTableValue())

    useEffect(() => {
        const [loaderSubscription, blastDeltaSubscription, valueObservable] = watchBlastDeltas(
            sessionContext,
            blastContext,
            properties,
            formMode
        )
        const valueSubscription = valueObservable.subscribe((newValue: any) => {
            try {
                const useTimezone: string = extraFieldProperties?.timeZone || timezone
                setTableValue(formatDate(parseISO(newValue || ''), properties.fieldConfig.dateFormat || 'DD-MM-YYYY', useTimezone))
            } catch (err) {
                console.error('-- error --', err)
                log.error(className, 'ERR001', 'Failed to parse date', err)
                setTableValue(format(parseISO(newValue || ''), 'dd-MM-yyyy HH:mm'))
            }
            setRefreshCounter(refreshCounter + 1)
        })
        return () => {
            unSubscribe(loaderSubscription)
            unSubscribe(blastDeltaSubscription)
            unSubscribe(valueSubscription)
        }
    }, [properties])

    useEffect(() => {
        extractTableValue()
    }, [_guslFormState?.counter])

    RunOnceEffect(() => {
        registerLocale('en-GB', enGB)
        setLocaleLoaded(true)

        let timezoneSubscription = sessionContext.watchTimezone().subscribe((changedTimezone: string) => {
            let _timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            if (changedTimezone !== BROWSER_TIMEZONE) {
                _timezone = changedTimezone
            }
            setTimezone(_timezone)
            setTableValue(extractTableValue(_timezone))
            setRefreshCounter(refreshCounter + 1)
            setDateFormat(getDateFormat(properties?.fieldConfig?.dateFormat, _timezone))
        })

        return () => {
            unSubscribe(timezoneSubscription)
        }
    })

    const fixDateFormatting = (dateFormat: string): string => {
        if (notDefined(dateFormat)) {
            return 'dd-MM-yyyy HH:mm'
        }
        return dateFormat.replace('DD', 'dd').replace('YYYY', 'yyyy').replace('YY', 'yy')
    }

    useEffect(() => {
        setRefreshCounter(refreshCounter + 1)
        setFormValue(() => convertToUTC())
        setTableValue(extractTableValue())
    }, [properties])

    const onFormModeChange = (mode: FormMode) => {
        setFormMode(mode)
    }

    const onRefresh = () => {}

    const doValidation = (fieldValue: Date | null): boolean => {
        setSubmitted(true)
        setErrorMessage(undefined)
        const hideField = shouldHide(formMode, properties.fieldConfig, fieldValue)
        if (!hideField) {
            // @ts-ignore
            const val = isDate(fieldValue) ? fieldValue.toISOString() : fieldValue
            const message = validate(properties.menuItem?.code, properties.fieldConfig.name, properties.fieldConfig.validators, 'date', val)
            // valueRef?.current?.value);
            if (message) {
                setErrorMessage(message)
                return false
            }
        }
        return true
    }

    RunOnceEffect(() => {
        // init
        properties.reference.changeMode = onFormModeChange
        properties.reference.doRefresh = onRefresh
        properties.reference.doValidation = doValidation
        properties.reference.register(properties.reference)
        return () => {
            // clean up
        }
    })

    function onInputChange(date: Date | null) {
        if (date) {
            if (!showTime) {
                const baseDate = new Date(date.getTime())
                setFormValue(baseDate)
                const utcDate = DateTime.fromObject(
                    {
                        year: baseDate.getFullYear(),
                        month: baseDate.getMonth() + 1,
                        day: baseDate.getDate(),
                        hour: showTime ? baseDate.getHours() : 0,
                        minute: showTime ? baseDate.getMinutes() : 0,
                        second: showTime ? baseDate.getSeconds() : 0,
                    },
                    { zone: 'utc' }
                )
                properties.onChange(properties.fieldConfig.name, utcDate.toISO())
            } else {
                setFormValue(date)
                properties.onChange(properties.fieldConfig.name, date.toISOString())
            }
        } else {
            setFormValue(null)
            properties.onChange(properties.fieldConfig.name, undefined)
        }
    }

    const renderTableView = (): React.ReactElement => {
        let prefix = ''
        if (extraFieldProperties.prefix) {
            prefix = extraFieldProperties.prefix
        }
        let suffix = ''
        if (extraFieldProperties.suffix) {
            suffix = extraFieldProperties.suffix
        }
        return (
            <TextFieldStyled textAlign={properties.fieldConfig.align} style={{ width: '100%' }}>
                <span key={'dt_' + properties.fieldConfig.name + '_' + refreshCounter}>
                    {prefix}
                    {tableValue}
                    {suffix}
                </span>
            </TextFieldStyled>
        )
    }

    const renderFormView = (): React.ReactElement => {
        const [hideField, disableField, required] = getSettings(formMode, properties.fieldConfig, formValue)

        if (hideField) {
            return <></>
        }
        if (formMode === FormMode.VIEW || disableField) {
            return (
                <>
                    <FloatingFormStyled key={'id_' + refreshCounter}>
                        <FieldContentWrapperStyled>
                            <FieldContentStyled>
                                <DateStyled submitted={submitted} noValue={!formValue}>
                                    {renderTableView()}
                                </DateStyled>
                            </FieldContentStyled>
                            {properties.fieldConfig.hint && <HintStyled>{properties.fieldConfig.hint}</HintStyled>}
                        </FieldContentWrapperStyled>
                        <FieldLabel properties={properties} />
                    </FloatingFormStyled>
                </>
            )
        }

        return (
            <DateFloatingFormStyled>
                <FieldContentWrapperStyled>
                    <FieldLabel properties={properties} />
                    <FieldContentStyled>
                        {localeLoaded && (
                            <DatePickerStyled
                                locale="en-GB"
                                submitted={submitted}
                                showTimeSelect={showTime}
                                noValue={!formValue}
                                selected={formValue}
                                dateFormat={dateFormat}
                                onChange={(date: Date | null) => onInputChange(date)}
                                placeholderText={fixDateFormatting(dateFormat)}
                            />
                        )}
                    </FieldContentStyled>
                    {properties.fieldConfig.hint && <HintStyled>{properties.fieldConfig.hint}</HintStyled>}
                    {submitted && errorMessage && <small className="error-message">{errorMessage}</small>}
                </FieldContentWrapperStyled>
            </DateFloatingFormStyled>
        )
    }

    return <>{properties.isTableView ? renderTableView() : renderFormView()}</>
}
